import http, { request } from './http';

export interface ComponentBomImportReferenceLink {
  url: string;
  title?: string;
  summary?: string;
}

export interface ComponentBomImportItem {
  name: string;
  code: string;
  legacyMaterialCode?: string | null;
  specModel?: string | null;
  materialProperty?: string | null;
  drawingNo?: string | null;
  unit?: string | null;
  usageNumerator?: string | null;
  usageDenominator?: string | null;
  warehouse?: string | null;
  issueWarehouse?: string | null;
  remark?: string | null;
  purchaser?: string | null;
  supplier?: string | null;
  leadTime?: string | null;
  categoryL1?: string | null;
  categoryL2?: string | null;
  categoryL3?: string | null;
  specSummary?: string | null;
  weightKg?: number | null;
  partWeight?: number | null;
  weightUnit?: string | null;
  suggestedImageUrl?: string | null;
  referenceLinks?: ComponentBomImportReferenceLink[];
  needsEnrichment?: boolean;
  confidence?: string | null;
}

export interface ComponentCatalogEntry {
  code: string;
  name: string;
  specModel?: string;
  size?: string;
  supplier?: string;
  cost?: number | null;
  unit?: string;
}

export interface ComponentCatalogSnapshot {
  format: string;
  total: number;
  components: ComponentCatalogEntry[];
}

export interface ComponentBomImportOverlapIdentical {
  bomCode?: string;
  catalogCode?: string;
  note?: string;
}

export interface ComponentBomImportOverlapConflict {
  conflictId: string;
  bomItem?: ComponentBomImportItem | null;
  catalogCode?: string;
  catalogName?: string;
  conflictReason?: string;
  suggestedNewCode?: string;
  suggestedNewName?: string;
}

export interface ComponentBomImportSimilar {
  similarId: string;
  bomItem?: ComponentBomImportItem | null;
  catalogCode?: string;
  catalogName?: string;
  comparison?: string;
  betterChoice?: string;
  betterLabel?: string;
  bomBetter?: boolean;
}

export interface ComponentBomImportAnalyzeResult {
  sessionId: string;
  success: boolean;
  summary: string;
  assistantReply: string;
  totalExtracted: number;
  thought?: { steps?: Array<{ key?: string; icon?: string; title?: string; text?: string }> };
  catalogSnapshot?: ComponentCatalogSnapshot;
  newItems: ComponentBomImportItem[];
  overlapIdentical: ComponentBomImportOverlapIdentical[];
  overlapConflicts: ComponentBomImportOverlapConflict[];
  similarItems: ComponentBomImportSimilar[];
  /** 兼容：等同 newItems */
  items: ComponentBomImportItem[];
}

export interface ComponentBomImportOverlapDecision {
  conflictId: string;
  action: 'skip' | 'use_existing' | 'create_new';
}

export interface ComponentBomImportConfirmRequest {
  sessionId: string;
  overlapDecisions: ComponentBomImportOverlapDecision[];
}

export interface ComponentBomImportConfirmResult {
  inserted: number;
  updated: number;
  skipped: number;
  componentIds: number[];
  message: string;
}

export interface ComponentEnrichmentStatus {
  id: number;
  enrichmentStatus: string | null;
}

function normalizeAnalyzeResult(raw: ComponentBomImportAnalyzeResult): ComponentBomImportAnalyzeResult {
  const newItems = raw.newItems ?? raw.items ?? [];
  return {
    ...raw,
    newItems,
    items: raw.items?.length ? raw.items : newItems,
    overlapIdentical: raw.overlapIdentical ?? [],
    overlapConflicts: raw.overlapConflicts ?? [],
    similarItems: raw.similarItems ?? [],
  };
}

export async function analyzeComponentBomImport(
  file: File,
  conversationId?: string,
): Promise<ComponentBomImportAnalyzeResult> {
  const body = new FormData();
  body.append('file', file);
  if (conversationId) body.append('conversationId', conversationId);
  const res = await request<{ success: boolean; data: ComponentBomImportAnalyzeResult }>(
    http.post('/components/bom-import/analyze', body, { timeout: 900000 }),
  );
  return normalizeAnalyzeResult(res.data);
}

export async function confirmComponentBomImport(
  payload: ComponentBomImportConfirmRequest,
): Promise<ComponentBomImportConfirmResult> {
  const res = await request<{ success: boolean; data: ComponentBomImportConfirmResult }>(
    http.post('/components/bom-import/confirm', payload, { timeout: 120000 }),
  );
  return res.data;
}

export async function getComponentEnrichmentStatus(
  ids: number[],
): Promise<ComponentEnrichmentStatus[]> {
  if (!ids.length) return [];
  const res = await request<{ success: boolean; data: ComponentEnrichmentStatus[] }>(
    http.get('/components/enrichment-status', {
      params: { ids: ids.join(',') },
    }),
  );
  return res.data ?? [];
}

export interface ComponentAutoFillStep {
  key?: string;
  icon?: string;
  title?: string;
  text?: string;
}

export interface ComponentAutoFillStartResult {
  jobId: string;
  componentId: number;
}

export interface ComponentAutoFillStatus {
  jobId: string;
  componentId: number;
  status: 'RUNNING' | 'COMPLETE' | 'FAILED' | string;
  progressText?: string;
  assistantReply?: string;
  thumbnailImported?: number;
  documentsImported?: number;
  steps?: ComponentAutoFillStep[];
}

export async function startComponentAutoFill(componentId: number): Promise<ComponentAutoFillStartResult> {
  const res = await request<{ success: boolean; data: ComponentAutoFillStartResult }>(
    http.post(`/components/${componentId}/auto-fill`, null, { timeout: 30000 }),
  );
  return res.data;
}

export async function getComponentAutoFillStatus(jobId: string): Promise<ComponentAutoFillStatus> {
  const res = await request<{ success: boolean; data: ComponentAutoFillStatus }>(
    http.get(`/components/auto-fill/${encodeURIComponent(jobId)}/status`),
  );
  return res.data;
}

export function buildDefaultOverlapDecisions(
  conflicts: ComponentBomImportOverlapConflict[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const c of conflicts) {
    if (c.conflictId) map[c.conflictId] = 'skip';
  }
  return map;
}

export function overlapDecisionsToPayload(
  decisions: Record<string, string>,
): ComponentBomImportOverlapDecision[] {
  return Object.entries(decisions).map(([conflictId, action]) => ({
    conflictId,
    action: action as ComponentBomImportOverlapDecision['action'],
  }));
}
